import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import { usePathname } from 'next/navigation';
import * as S from './TabBar.styles';
import { useScrollVisibility } from '@/hooks/useScrollVisibility';
import { tabBarLinks } from '@/components/TabBar/TabBar.constants';
import { useHasUnreadMessages } from '@/utils/pubnub/useHasUnreadMessages';
import { isCurrentPage } from '@/components/TabBar/TabBar.helpers';
import { usePageTopControlsStore } from '@/store/usePageTopControlsStore';
import { ROUTES } from '@/constants/routes';
import { useAuthStore } from '@/store/useAuthStore';
import { useBridgeState } from '@/bridge/hooks';
import { getNewAdPageHref } from '@/utils/helpers';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { Button } from '@/components/UI/Button/Button';
import { TabBarIcon, TabBarIconName } from '@/components/UI/Icons/TabBarIcon';
import { useBannerStore } from '@/store/useBannerStore/useBannerStore';

export const TabBar = () => {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const pathname = usePathname();
  const client = useApolloClient();
  const hasUnreadMessages = useHasUnreadMessages();
  const reloadBanners = useBannerStore(state => state.reloadBanners);
  const [isAuthLoaded, isAuthenticated] = useAuthStore(state => [
    state.isAuthLoaded,
    state.isAuthenticated,
  ]);
  const { isVisible, isEndOfPage, scrollPos } = useScrollVisibility();
  const topControlsHeight = usePageTopControlsStore(
    state => state.topControlsHeight
  );
  const { isWebView } = useBridgeState();
  const $isVisible =
    isWebView || scrollPos <= topControlsHeight || isVisible || isEndOfPage;
  const customPush = useCustomPush();
  const isHomePage = pathname === ROUTES.home;

  async function handleClick(
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    href: string
  ) {
    if (href === ROUTES.home) {
      e.preventDefault();
      if (isHomePage) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        reloadBanners();
        await client.resetStore();
      } else {
        await client.clearStore();
      }

      await customPush(ROUTES.home);
    }
  }

  return (
    <>
      <S.TabBarBoxForSize />
      <S.TabBarBox $isVisible={$isVisible} className="tab-bar-container">
        {tabBarLinks(t, isAuthLoaded, isAuthenticated, asPath).map(
          ({ href, iconName, isBig, text, isDisabled, dataStat }) => {
            const disabled = isDisabled || !isAuthLoaded;
            return (
              <S.TabBarLink
                $isDisabled={disabled}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : undefined}
                key={iconName}
                href={href}
                onClick={e => handleClick(e, href)}
                {...(href === getNewAdPageHref(asPath) && {
                  as: Button,
                  href: undefined,
                  appearance: 'transparent',
                  hasPadding: false,
                  onClick: () => {
                    customPush(href);
                  },
                })}
                {...(dataStat && { 'data-stat': dataStat })}
                className="tab-bar__link"
              >
                <S.TabBarLinkIconContainer $size={isBig ? 45 : 20}>
                  <TabBarIcon
                    name={iconName as TabBarIconName}
                    isActive={isCurrentPage(href, asPath)}
                  />
                  {href === '/messages' && hasUnreadMessages && (
                    <S.TabBarUnreadIcon />
                  )}
                </S.TabBarLinkIconContainer>
                <S.TabBarLinkText $isActive={isCurrentPage(href, asPath)}>
                  {text}
                </S.TabBarLinkText>
              </S.TabBarLink>
            );
          }
        )}
      </S.TabBarBox>
    </>
  );
};
